import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';

//
// GENERIC INTERFACES
//
export interface KeyValueInterface {
  key: number;
  value: string;
}

export interface KeystringValueInterface {
  key: string;
  value: string;
}

export interface JsonResponseInterface {
  status: number;
  message: string;
}

export interface JsonArrayResponseInterface {
  status: number;
  message: string;
  results: any[];
}

export interface SelectEntityOption {
  id: number;
  label: string;
}

//
// TOKEN INTERFACE
//
export interface TokenInterface {
  token: string;
}

export interface JsonTokenInterface extends JsonResponseInterface {
  results: TokenInterface;
}

//
// USER LOGGED INFO INTERFACE
//
export interface HeaderstylesInterface {
  headerlogouri?: string;
  headerheight?: string;
  headerbackgroundcolor?: string;
  headertextcolorclassname?: string;
  mandanttitle?: string;
}

export interface BodystylesInterface {
  bodybackgrounduri?: string;
}

export interface ToolInterface {
  toolid: number;
  toolkeyword: string;
  toolclassname?: string;
  toolpath?: string; // starting slash and no end slash /ext/12/zenithAOS2DEV
  sort?: number;
  toolkeywordtranslation?: string;
  toolenvname?: string;
  clientid?: number;
  activeorderlisttab?: boolean;
  orderlistapi?: string;
  orderlistdetailapi?: string;
}

export interface OrderDetailLineItemInterface {
  quantity?: number;
}

export interface OrderDetailInterface {
  id: number;
  ordernr: string;
  orderdate: any;
  useremail?: string;
  language?: string;
  items?: any;
  options?: any;
}

export interface JsonArrayOrderDetailResponseInterface {
  status: number;
  message: string;
  results: OrderDetailInterface;
}

export interface JsonArrayHtmlCode {
  status: number;
  message: string;
  results: string;
}

export interface UserinfoInterface {
  id?: number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  mandantLabel?: string;
  roles?: string[];
  tools?: any[];
  cavokusergroups?: any[];
  last_login?: any;
}

export interface MarketInterface {
  id: number;
  active: number;
  label: string;
}

export interface MarketGroupInterface {
  id: number;
  active: number;
  label: string;
  markets: [];
}

export interface NewsInterface {
  newslabel?: string;
  newstitle?: string;
  newscontent?: string;
  imgpath?: string;
  newstype?: string;
  newseventdate?: string;
}

export interface LoggedinfoInterface {
  headerstyles?: HeaderstylesInterface;
  bodystyles?: BodystylesInterface;
  tools?: ToolInterface[];
  userinfo: UserinfoInterface;
  languages?: KeystringValueInterface[];
  news?: NewsInterface[];
  additionalCSS?: string;
  newsPosition: string;
}

export interface JsonResponseLoggedinfoInterface extends JsonResponseInterface {
  results: LoggedinfoInterface;
}

//
// USER NOT LOGGED INTERFACE
//
export interface NotLoggedinfoInterface {
  frontlogouri?: string;
  backgrounduri?: string;
  bodystyles?: BodystylesInterface;
  mandanttitle?: string;
  maintenance_mode?: { [propName: string]: any; };
}

export interface JsonResponseNotLoggedinfoInterface extends JsonResponseInterface {
  results: NotLoggedinfoInterface;
}


//
// TOOL URL TOKEN
//
export interface ToolurltokenInterface {
  toolurltoken: string;
}

export interface JsonResponseToolurltokenInterface extends JsonResponseInterface {
  results: ToolurltokenInterface;
}

//
// HELP INTERFACE
//
export interface HelpSectionInterface {
  id: string;
  title: string;
  content: string;
  sort: number;
}

export interface JsonResponseHelpInterface extends JsonResponseInterface {
  results: HelpSectionInterface[];
}

//
// USER INFO INTERFACE
//
// export interface UserinfoInterface {
//   username?: string;
//   roles?: string[];
// }

export interface JsonUserinfoInterface extends JsonResponseInterface {
  results: UserinfoInterface;
}

export interface MandantNewsInterface extends JsonResponseInterface {
  label?: string;
  imgpath?: string;
  content?: MandantNewsInterfaceContent;
  newstitle?: string;
  newscontent?: string;
}

export interface MandantNewsInterfaceContent {
  1178?: number;
  1179?: number;
}


export interface JsonResponseListofNewsInterface extends JsonResponseInterface {
  results: NewsInterface[];
}

/**
 * Admin logs
 */
export interface Log {
  id: number;
  level: string;
  service: string;
  username: string;
  userid: number;
  created: string;
  message: string;
  data: string;
  userAgent: string;
  userIp: string;
}

export interface JsonResponseLogsInterface extends JsonResponseInterface {
  results: Log[];
}

/**
 * App clients
 */
export interface AppClient {
  id: number;
  label: string;
  active: 0 | 1;
}

export interface JsonResponseAppClientsInterface extends JsonResponseInterface {
  results: AppClient[];
}

/**
 * Services
 */
export interface Service {
  label: number;
  url: string;
  status: 'up' | 'down';
}

export interface JsonResponseServicesInterface extends JsonResponseInterface {
  results: Service[];
}


/**
 * User aos specific tool configs
 */
export interface ToolConfigs {
  id?: number;
  toolid: number;
  active: boolean;
  classname: string;
  toolkeyword?: string;
  envname?: string;
  username?: string;
  mandantlabel?: string;
  mandantkey?: string;

  toolgroupflagkey: string;
  mandantflagkey: string;
  envnameflagkey: string;
  apiaosroot?: string;

  aosmarkets?: any;
  aoscompanies?: any;

  companyid?: number;
  roles?: KeystringValueInterface[];

  selecteduserroles?: KeystringValueInterface[];
  alluserroles?: KeystringValueInterface[];

  selectedusermarkets?: KeystringValueInterface[];
  allusermarkets?: KeystringValueInterface[];

  selectedusercompanies?: KeystringValueInterface[];
  allusercompanies?: KeystringValueInterface[];
}

export interface JsonResponseToolConfigsInterface extends JsonResponseInterface {
  results: ToolConfigs;
}


/**
 * Orders
 */
export type OrderType = 'aos' | 'dos' | 'webshop' | 'instore' | 'business-card';

export interface AosOrderInterface {
  id: number;
  company_label: string;
  companyAdditionalLine1: string;
  companyAdditionalLine2: string;
  companyStreet: string;
  companyPoBox: string;
  companyZip: string;
  companyPlace: string;
  companyCountry: string;
  companyPhone: string;
  companyFax: string;
  companyEmail: string;
  country_label: string;
  userFullName: string;
  status: string;
  created: {
    date: string
  };
  nr: string;
  orderFileName: string;
  magazine_printTitle: string;
  data: string;
  d_unit: string;
  d_width: number;
  d_height: number;
  productTypeLabel: string;
  orderRemarks: string;
  publicationDate: string;
  shipmentDate: string;
  d_printType: string;
}

export interface AosOrderDataInterface {
  orderFileName: string;
  addressFields: string;
  publicationDate: string;
  factorWidth: number;
  factorHeight: number;
  factorBleed: number;
  poolRemarks: string;
  orderRemarks: string;
  printTypeColor: string;
  deliverMethod: string;
}

export interface DosOrderInterface {
  id: number;
  documentOrderNumber: string;
  email: string;
  jsondata: string;
  created: {
    date: string;
  };
  delivery: {
    date: string;
  };
  fullName: string;
  remark: string;
  machineName: string;
  serialNumber: string;
  sapNumber: string;
  authServerUsername: string;
  authServerUserId: number;
}

export interface DecodedDosOrderDataInterface {
  ordermachine: string;
  orderdata: {
    [key: string]: {
      _dbid: number;
      _documentsetname: string;
      _documentsetthumbnail: string;
      _active: boolean;
      _machinelanguages: DosOrderMachineLanguageInterface[];
    };
  };
  orderform: {
    deliveryDate: string;
    sapNumber: string;
    serialNumber: string;
    remark: string;
  };
}

export interface DosOrderMachineLanguageInterface {
  _languageid: number;
  _languageiso2: string;
  _languagename: string;
  _quantity: number;
  _documentfolderid: number;
  _documentsetid: number;
  _documentfolder: {
    _dbid: number;
    _documentfoldername: string;
    _folders: DosOrderMachineLanguageFolderInterface[];
  };
  _machineid: number;
  _machinelanguageid: number;
}

export interface DosOrderMachineLanguageFolderInterface {
  _documentitems: {
    _documentstoselect: any[];
    _selecteddocuments: any[];
    _dbid: number;
    _docnumber: string;
    _docname: string;
    _doctype: string;
    _draggable: boolean;
    _removable: boolean;
    _filename: string;
    _randomid: string;
  }[];
}

//
// i18n
//
export interface I18nBundleInterface {
  en: KeystringValueInterface[];
  de: KeystringValueInterface[];
  fr: KeystringValueInterface[];
}

export interface JsonI18nBundleInterface extends JsonResponseInterface {
  results: I18nBundleInterface;
}

export interface BusinessCardOrderInterface {
  id: string;
  language: string;
  mandant: string;
  orderdate: string;
  ordernr: string;
  useremail: string;
  username: string;
}

export interface PackshotVideoLonginesOrderInterface {
  id: string;
  language: string;
  mandant: string;
  orderdate: string;
  ordernr: string;
  useremail: string;
  username: string;
}

export interface BusinessCardOrderDetailInterface {
  ordernr: string;
  orderdate: string;
  mandant: string;
  username: string;
  useremail: string;
  language: string;
  items: BusinessCardOrderDetailItem[];
  options: {
    deliveryAddress: {
      deliveryAddressId: string;
      deliveryAddressArray: {
        id: string;
        label: string;
        forename: string;
        surename: string;
        companyName: string;
        addressLine1: string;
        addressLine2: string;
        zip: string;
        city: string;
        department: string;
        country: string;
        phone: string;
        email: string;
        comments: string;
      }
    },
    [key: string]: any; // To cover all dynmic fields from the order form
  };
}

export interface BusinessCardOrderDetailItem {
  quantity: number;
  options: [];
  documentConfigId: string;
  documentConfigArray: {
    id: string;
    active: boolean;
    mandant: string;
    templateId: string;
    doDocumentconfigtemplateArray: {
      id: string;
      active: boolean;
      mandant: string;
      label: string;
      documentTypeId: string;
      documenttypeArray: {
        id: string;
        active: boolean;
        mandant: string;
        label: string;
        key: string;
        displayColumns: string; // comma separated field names
        categories: [];
        options: {
          [key: string]: any;
        };
      };
      pageTemplateConfigs: {
        id: number;
        label: string;
        templateFileName: string;
        templateFilePath: string;
        templateScriptFileName: string
      }[];
      options: {
        [key: string]: any;
      };
    };
    fieldValues: {
      [key: string]: any;
    };
    categories: [];
  };
}

export interface PackshotVideoLonginesOrderDetailInterface {
  [key: string]: any;
}

export interface PackshotVideoLonginesOrderDetailItem {
  [key: string]: any;
}

//
// API INTERFACES
//
export abstract class AbstractBffApi {
  constructor(protected http: HttpClient, protected serverRoot: string, protected simorigin: string) {
  }

  abstract login(encryptedCredential: string): Observable<JsonTokenInterface>;

  abstract logout(): Observable<any>;

  abstract checkuser(): Observable<JsonUserinfoInterface>;

  abstract getsiteinfo(): Observable<JsonResponseNotLoggedinfoInterface>;

  abstract getloggedinfo(languagekey: string): Observable<JsonResponseLoggedinfoInterface>;

  // the languagekey here is the iso-639-1, two letter key (lowercase) of a language
  abstract gettoollogin(toolid: number, toolkeyword: string, languagekey: string, params: string): Observable<any>;

  abstract setAOSSid(sid: string, toolurl: string): Observable<any>;

  abstract getAssets(url: string): string;

  // deprecated, use getmandantnews2
  abstract getmandantnews(mandanid: string, lngid: number, origin: string,
                          simorigin: string): Observable<MandantNewsInterface>;

  abstract getmandantnews2(languagekey: string): Observable<JsonResponseListofNewsInterface>;

  abstract gethelp(languagekey: string): Observable<JsonResponseHelpInterface>;

  abstract getClients(): Observable<JsonResponseAppClientsInterface>;

  abstract getLastLogs(): Observable<JsonResponseLogsInterface>;

  abstract getToolkeywordTranslation(languagekey: string): Observable<any>;

  abstract getServices(): Observable<JsonResponseServicesInterface>;

  abstract getBlockedServices(): Observable<JsonResponseServicesInterface>;

  abstract getLngUserPreference(): Observable<any>;

  abstract getAOSOrderProductTypes(): Observable<any>;

  abstract getAOSOrderUsers(): Observable<any>;

  abstract getAOSOrderCountries(): Observable<any>;

  abstract getAOSOrderCompanies(): Observable<any>;

  abstract geteditprofilviewinterface(languagekey: string): Observable<any>;

  abstract requestPasswordReset(usernameOrEmail: string): Observable<any>;

  abstract setNewPassword(encryptedNewPasswordData: string): Observable<any>;

  abstract searchOrders(searchObject: any): Observable<any>;

  abstract getRecentOrders(orderType: OrderType): Observable<any>;

  abstract getAOSRoles(usertoolid: string): Observable<any>;

  abstract getAOSAppClients(usertoolid: string): Observable<any>;

  abstract getAOSCompanies(apiaosroot: string, username: string, mandantkey: string): Observable<any> ;

  abstract getAOSToolRoles(apiaosroot: string, username: string, mandantkey: string): Observable<any> ;

  abstract getAOSToolRights(apiaosroot: string, username: string, mandantkey: string): Observable<any> ;

  abstract setAOSUserTool(apiaosroot: string, body: any): Observable<any>;
}
